import type {
  GdProjectInvoiceOption,
  HeroProjectInvoiceOption,
} from "@/features/timesheetFlow/timesheetFlowApi";
import { api } from "@/lib/apiClient";

export type LieferscheinBillingStatus =
  | "signed"
  | "assigned"
  | "invoice_draft"
  | "invoice_created"
  | "archived"
  | string;

export interface LieferscheinSummary {
  id: number;
  display_number: string;
  dokument_nummer?: string | null;
  status: string;
  billing_status: LieferscheinBillingStatus;
  auftrag_id: number | null;
  auftrag_nummer?: string | null;
  ordnernummer?: string | null;
  auftrag_beschreibung?: string | null;
  kunde_name?: string | null;
  betreff?: string | null;
  customer_signed_at?: string | null;
  billing_assignee_login?: string | null;
  billing_assignee_name?: string | null;
  billing_assigned_at?: string | null;
  billing_archived_at?: string | null;
  gd_rechnung_id?: number | null;
  hero_invoice_document_id?: string | null;
  hero_invoice_customer_document_id?: string | null;
  hero_invoice_number?: string | null;
  rechnung_nummer?: string | null;
  rechnung_status?: string | null;
  billing_question_count?: number;
  billing_question_open_count?: number;
  has_billing_questions?: boolean;
  billing_questions_open?: boolean;
  is_new_for_billing?: boolean;
  sharepoint_web_url?: string | null;
  sharepoint_signed_pdf_share_url?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  project_completed?: boolean;
  at_contact?: boolean | null;
  at_contact_at?: string | null;
  belegdatum?: string | null;
  lieferdatum?: string | null;
  summe_brutto?: number | null;
  invoice_value?: number | null;
  auftragsnummer?: string | null;
  jahr?: number | null;
  hero_project_id?: number | null;
  ordnerpfad?: string | null;
  sharepoint_folder_id?: string | null;
  legacy_import?: boolean | null;
  ordner_beschreibung?: string | null;
  kunde?: string | null;
  ort?: string | null;
  ansprechpartner_vorname?: string | null;
  ansprechpartner_nachname?: string | null;
  ansprechpartner?: string | null;
  auftrag_display?: string | null;
}

export interface LieferscheinAttachment {
  id: number;
  dokument_id: number;
  filename: string;
  content_type: string;
  size_bytes: number;
  role: "signed_pdf" | "scan_attachment" | string;
  source?: string | null;
  created_at?: string | null;
}

export interface LieferscheinBillingQuestionItem {
  id: number;
  position_id?: number | null;
  question_text?: string | null;
  response_text?: string | null;
  price?: number | string | null;
  responded_at?: string | null;
  material_text?: string | null;
  material_menge?: number | null;
  material_einheit?: string | null;
}

export interface LieferscheinBillingQuestion {
  id: number;
  dokument_id: number;
  created_by_login: string;
  assigned_to_group: string;
  status: string;
  question_text?: string | null;
  response_text?: string | null;
  created_at: string;
  responded_at?: string | null;
  items: LieferscheinBillingQuestionItem[];
}

export interface LieferscheinDetail extends LieferscheinSummary {
  positions: Array<{
    id: number;
    position_nr?: number | null;
    position_typ?: string | null;
    bezeichnung?: string | null;
    menge?: number | null;
    einheit?: string | null;
    einzelpreis_netto?: number | null;
  }>;
  attachments: LieferscheinAttachment[];
  billing_questions: LieferscheinBillingQuestion[];
}

export interface LieferscheinInvoiceCandidates {
  primary: { id: number; display_number: string; status: string; gd_rechnung_id?: number | null };
  candidates: Array<{ id: number; display_number: string; status: string }>;
  has_additional: boolean;
}

export async function listLieferscheineSummary(opts?: {
  includeArchived?: boolean;
}): Promise<LieferscheinSummary[]> {
  const qs = opts?.includeArchived ? "?include_archived=true" : "";
  return api.get<LieferscheinSummary[]>(`/lieferscheine/summary${qs}`);
}

export async function markLieferscheinAtContact(
  dokumentId: number,
  atContact: boolean,
): Promise<void> {
  await api.post(`/lieferscheine/${dokumentId}/mark-at-contact`, { at_contact: atContact });
}

export function lieferscheinAtContactFields(
  atContact: boolean,
): Pick<LieferscheinSummary, "at_contact" | "at_contact_at"> {
  return {
    at_contact: atContact,
    at_contact_at: atContact ? new Date().toISOString() : null,
  };
}

export interface LieferscheinColumnSettings {
  desktop: { columns: string[]; widths: Record<string, number> } | null;
}

const LIEFERSCHEIN_TABLE_KEY = "lieferscheine";

export async function getLieferscheinColumnSettings(): Promise<LieferscheinColumnSettings> {
  const res = await api.get<{ settings: LieferscheinColumnSettings | null }>(
    `/me/table-settings/${LIEFERSCHEIN_TABLE_KEY}`,
  );
  return { desktop: res?.settings?.desktop ?? null };
}

export async function saveLieferscheinColumnSettings(
  settings: LieferscheinColumnSettings,
): Promise<LieferscheinColumnSettings> {
  const res = await api.post<{ settings: LieferscheinColumnSettings }>(
    `/me/table-settings/${LIEFERSCHEIN_TABLE_KEY}`,
    { settings },
  );
  return { desktop: res?.settings?.desktop ?? settings.desktop };
}

export async function getLieferscheinDetail(id: number): Promise<LieferscheinDetail> {
  return api.get<LieferscheinDetail>(`/lieferscheine/${id}`);
}

export async function assignLieferscheinBilling(
  id: number,
  assigneeLogin: string,
): Promise<void> {
  await api.post(`/lieferscheine/${id}/assign-billing`, { assignee_login: assigneeLogin });
}

export async function markLieferscheinBillingSeen(id: number): Promise<void> {
  await api.post(`/lieferscheine/${id}/mark-billing-seen`, {});
}

export async function archiveLieferschein(id: number): Promise<void> {
  await api.post(`/lieferscheine/${id}/archive`, {});
}

export async function unarchiveLieferschein(id: number): Promise<void> {
  await api.post(`/lieferscheine/${id}/unarchive`, {});
}

export async function listLieferscheinBillingQuestions(
  id: number,
  includeAnswered = true,
): Promise<LieferscheinBillingQuestion[]> {
  const qs = includeAnswered ? "?include_answered=true" : "";
  return api.get(`/lieferscheine/${id}/billing-questions${qs}`);
}

export async function createLieferscheinBillingQuestions(
  id: number,
  payload: {
    general_question?: string;
    position_questions?: Array<{ position_id?: number; question_text: string }>;
  },
): Promise<void> {
  await api.post(`/lieferscheine/${id}/billing-questions`, payload);
}

export async function respondLieferscheinBillingQuestion(
  dokumentId: number,
  questionId: number,
  payload: {
    response_text?: string;
    items?: Array<{ item_id: number; response_text?: string; price?: number }>;
  },
): Promise<void> {
  await api.post(
    `/lieferscheine/${dokumentId}/billing-questions/${questionId}/respond`,
    payload,
  );
}

export async function listLieferscheinInvoiceCandidates(
  id: number,
): Promise<LieferscheinInvoiceCandidates> {
  return api.get(`/lieferscheine/${id}/dokumente/invoice-candidates`);
}

export async function listLieferscheinProjectInvoices(
  id: number,
): Promise<GdProjectInvoiceOption[]> {
  const data = await api.get<GdProjectInvoiceOption[] | { items?: GdProjectInvoiceOption[] }>(
    `/lieferscheine/${id}/dokumente/project-invoices`,
  );
  if (Array.isArray(data)) return data;
  return Array.isArray(data?.items) ? data.items : [];
}

export async function listLieferscheinHeroInvoices(
  id: number,
): Promise<HeroProjectInvoiceOption[]> {
  const data = await api.get<HeroProjectInvoiceOption[]>(
    `/lieferscheine/${id}/hero/project-invoices`,
  );
  return Array.isArray(data) ? data : [];
}

export async function linkLieferscheinRechnung(
  id: number,
  gdRechnungId: number,
): Promise<{
  ok?: boolean;
  already_linked?: boolean;
  gd_rechnung_id?: number;
  display_number?: string | null;
  billing_status?: string;
}> {
  return api.post(`/lieferscheine/${id}/dokumente/link-rechnung`, {
    gd_rechnung_id: gdRechnungId,
  });
}

export async function linkLieferscheinHeroRechnung(
  id: number,
  body: { invoice_document_id: string | number; invoice_customer_document_id?: string | number },
): Promise<{
  ok?: boolean;
  already_linked?: boolean;
  invoice_number?: string | null;
  billing_status?: string;
}> {
  return api.post(`/lieferscheine/${id}/hero/link-invoice`, body);
}

export async function createLieferscheinInvoiceDraft(
  id: number,
  dokumentIds?: number[],
): Promise<{ ok: boolean; gd_rechnung_id: number; already_exists?: boolean }> {
  return api.post(`/lieferscheine/${id}/dokumente/invoice-draft`, {
    dokument_ids: dokumentIds ?? null,
  });
}

export function lieferscheinAttachmentUrl(dokumentId: number, attachmentId: number): string {
  return `/dokumente/${dokumentId}/attachments/${attachmentId}`;
}

export function lieferscheinSignedPdfUrl(dokumentId: number): string {
  return `/dokumente/${dokumentId}/pdf/signed-latest`;
}

export function lieferscheinBillingStatusLabel(status: string): string {
  switch (status) {
    case "signed":
      return "Geliefert";
    case "assigned":
      return "Zugewiesen";
    case "invoice_draft":
      return "Rechnungsentwurf";
    case "invoice_created":
      return "Rechnung";
    case "archived":
      return "Archiviert";
    case "geliefert":
      return "Geliefert";
    case "versendet":
      return "Versendet";
    case "erstellt":
      return "Erstellt";
    default:
      return status || "—";
  }
}

export function lieferscheinBillingStatusClass(status: string): string {
  switch (status) {
    case "signed":
    case "geliefert":
      return "bg-status-green/15 text-status-green";
    case "assigned":
      return "bg-status-orange/15 text-status-orange";
    case "invoice_draft":
      return "bg-status-blue/15 text-status-blue";
    case "invoice_created":
      return "bg-fill-tertiary text-label-secondary";
    case "archived":
      return "bg-fill-quaternary text-label-tertiary";
    default:
      return "bg-fill-tertiary text-label-secondary";
  }
}
